import React, { useState } from "react";
import { Link } from "react-router-dom";
import ScrollReveal from "./ScrollReveal";
import { Check, Package } from "lucide-react";
import { publicAsset } from "@/lib/publicAsset";
import VipKitModal from "./VipKitModal";
import { VIP_DISCORD_TICKET_URL } from "@/components/vip-simulator/vipInvoiceExport";
import { getVipKitPlan } from "@/data/vipKitConfig";
import { useI18n } from "@/i18n/I18nProvider";

/** WebP em `public/images/vips/` — evita imports fora de `src/` (ModuleScope / ficheiros em falta). Usadas como fundo dos cartões. */
const VIP_ART = {
    bronze: publicAsset("/images/vips/bronze.webp"),
    prata: publicAsset("/images/vips/prata.webp"),
    ouro: publicAsset("/images/vips/ouro.webp"),
    apoiador: publicAsset("/images/vips/apoiador.webp"),
    personalizado: publicAsset("/images/vips/personalizado.webp"),
};

/** Textos bilingues `{ pt, en }` — resolvidos com `l()` no render. */
const KIT_EQUIP_TITLE = { pt: "Equipáveis", en: "Wearables" };
/** Mochilas nativas do Rust (sem plugin de backpack): pequena no Bronze, grande nos restantes. */
const B_SMALL_BACKPACK = { pt: "Mochila pequena no kit", en: "Small Backpack in the kit" };
const B_LARGE_BACKPACK = { pt: "Mochila grande no kit", en: "Large Backpack in the kit" };
const ITEM_SMALL_BACKPACK = { pt: "1x Mochila Pequena", en: "1x Small Backpack" };
const ITEM_LARGE_BACKPACK = { pt: "1x Mochila Grande", en: "1x Large Backpack" };
const B_AUTO_FARM = { pt: "Farme automatico (farme em geral)", en: "Auto-farm (all gathering)" };
const B_NO_POWER = {
    pt: "Sentinelas e anti-aérea sem energia (com botão)",
    en: "Auto Turrets and SAM Sites without power (button toggle)",
};
const B_XP_COINS = {
    pt: "Acesso ao sistema de xp e coins exclusivo de vips para reclamação de recompensas (Em breve)",
    en: "Access to the VIP-exclusive XP & coins system to claim rewards (Coming soon)",
};
const PROMO_10_OFF = { pt: "10% OFF", en: "10% OFF" };

/** Secções do kit na face de trás: title null = bloco principal */
const TIERS = [
    {
        id: "bronze",
        name: { pt: "Bronze", en: "Bronze" },
        line: { pt: "Ideal para começar", en: "Perfect to get started" },
        weekly: "R$ 7,90",
        monthly: "R$ 19,90",
        benefits: [
            { pt: "Kit Base", en: "Base Kit" },
            B_SMALL_BACKPACK,
            { pt: "Tag [Bronze] no chat", en: "[Bronze] tag in chat" },
        ],
        kitSections: [
            {
                title: null,
                items: [
                    { pt: "1x Rifle Semiautomático", en: "1x Semi-Automatic Rifle" },
                    { pt: "1x Fuzil M39", en: "1x M39 Rifle" },
                    { pt: "3x Kits Médicos Grandes", en: "3x Large Medkits" },
                    { pt: "13x Bandagens", en: "13x Bandages" },
                    { pt: "25x Seringas Médicas", en: "25x Medical Syringes" },
                    { pt: "10x Barricadas de Madeira", en: "10x Wooden Barricade Covers" },
                    { pt: "200x Munições de Rifle 5.56", en: "200x 5.56 Rifle Ammo" },
                    { pt: "50x Panos", en: "50x Cloth" },
                    { pt: "1x Saco de Dormir", en: "1x Sleeping Bag" },
                ],
            },
            {
                title: KIT_EQUIP_TITLE,
                items: [
                    { pt: "1x Jaqueta", en: "1x Jacket" },
                    { pt: "1x Botas", en: "1x Boots" },
                    { pt: "1x Calça", en: "1x Pants" },
                    { pt: "1x Bandana", en: "1x Bandana Mask" },
                    { pt: "1x Capacete de Manifestação", en: "1x Riot Helmet" },
                    { pt: "1x Saia de Placas de Trânsito", en: "1x Road Sign Kilt" },
                    ITEM_SMALL_BACKPACK,
                ],
            },
        ],
        accent: "#A97142",
    },
    {
        id: "prata",
        name: { pt: "Prata", en: "Silver" },
        line: { pt: "Mais justo", en: "The fair deal" },
        weekly: "R$ 11,90",
        monthly: "R$ 29,90",
        benefits: [
            { pt: "Kit Prata", en: "Silver Kit" },
            B_LARGE_BACKPACK,
            { pt: "Tag [Prata] no chat", en: "[Silver] tag in chat" },
            B_AUTO_FARM,
            B_NO_POWER,
            B_XP_COINS,
        ],
        kitSections: [
            {
                title: null,
                items: [
                    { pt: "1x Rifle de Assalto", en: "1x Assault Rifle" },
                    { pt: "1x Fuzil M39", en: "1x M39 Rifle" },
                    { pt: "10x Kits Médicos Grandes", en: "10x Large Medkits" },
                    { pt: "25x Bandagens", en: "25x Bandages" },
                    { pt: "25x Seringas Médicas", en: "25x Medical Syringes" },
                    { pt: "10x Barricadas de Madeira", en: "10x Wooden Barricade Covers" },
                    { pt: "200x Bala de pistola", en: "200x Pistol Bullets" },
                    { pt: "100x Munição 5.56 Incendiária de Rifle", en: "100x Incendiary 5.56 Rifle Ammo" },
                    { pt: "100x Panos", en: "100x Cloth" },
                    { pt: "2x Saco de Dormir", en: "2x Sleeping Bags" },
                ],
            },
            {
                title: KIT_EQUIP_TITLE,
                items: [
                    { pt: "1x Jaqueta", en: "1x Jacket" },
                    { pt: "1x Botas", en: "1x Boots" },
                    { pt: "1x Calça", en: "1x Pants" },
                    { pt: "1x Luvas de Sinal de Trânsito", en: "1x Roadsign Gloves" },
                    { pt: "1x Capacete de Lata de Café", en: "1x Coffee Can Helmet" },
                    { pt: "1x Saia de Placas de Trânsito", en: "1x Road Sign Kilt" },
                    ITEM_LARGE_BACKPACK,
                ],
            },
        ],
        accent: "#C9CED1",
    },
    {
        id: "ouro",
        name: { pt: "Ouro", en: "Gold" },
        line: { pt: "Melhor custo-benefício", en: "Best value" },
        weekly: "R$ 15,21",
        monthly: "R$ 35,91",
        promo: {
            label: PROMO_10_OFF,
            weekly: { before: "R$ 16,90", after: "R$ 15,21" },
            monthly: { before: "R$ 39,90", after: "R$ 35,91" },
        },
        benefits: [
            { pt: "Kit Ouro", en: "Gold Kit" },
            B_LARGE_BACKPACK,
            { pt: "Tag [Ouro] no chat", en: "[Gold] tag in chat" },
            B_AUTO_FARM,
            B_NO_POWER,
            B_XP_COINS,
        ],
        kitSections: [
            {
                title: null,
                items: [
                    { pt: "1x Rifle de Assalto", en: "1x Assault Rifle" },
                    { pt: "1x Rifle De Precisão", en: "1x Bolt Action Rifle" },
                    { pt: "100x Kits Médicos Grandes", en: "100x Large Medkits" },
                    { pt: "100x Bandagens", en: "100x Bandages" },
                    { pt: "100x Seringas Médicas", en: "100x Medical Syringes" },
                    { pt: "10x Muro Alto de Pedra", en: "10x High External Stone Walls" },
                    { pt: "200x Munições de Rifle 5.56", en: "200x 5.56 Rifle Ammo" },
                    { pt: "100x Munição 5.56 Incendiária de Rifle", en: "100x Incendiary 5.56 Rifle Ammo" },
                    { pt: "200x Panos", en: "200x Cloth" },
                    { pt: "5x Saco de Dormir", en: "5x Sleeping Bags" },
                    { pt: "2x Chá de Minério Puro", en: "2x Pure Ore Tea" },
                    { pt: "50x Chá de Cura Puro", en: "50x Pure Max Health Tea" },
                    { pt: "2x Chá de Sucata Puro", en: "2x Pure Scrap Tea" },
                    { pt: "100x Abóbora", en: "100x Pumpkins" },
                ],
            },
            {
                title: KIT_EQUIP_TITLE,
                items: [
                    { pt: "1x Colete de Metal", en: "1x Metal Chest Plate" },
                    { pt: "1x Botas", en: "1x Boots" },
                    { pt: "1x Calça", en: "1x Pants" },
                    { pt: "1x Blusão", en: "1x Hoodie" },
                    { pt: "1x Luvas Táticas", en: "1x Tactical Gloves" },
                    { pt: "1x Máscara de Metal", en: "1x Metal Facemask" },
                    { pt: "1x Saia de Placas de Trânsito", en: "1x Road Sign Kilt" },
                    ITEM_LARGE_BACKPACK,
                ],
            },
        ],
        accent: "#FFB800",
    },
    {
        id: "apoiador",
        name: { pt: "Apoiador", en: "Supporter" },
        line: { pt: "O mais valioso e comprado", en: "Most valuable & best-selling" },
        weekly: "R$ 26,99",
        monthly: "R$ 53,91",
        promo: {
            label: PROMO_10_OFF,
            weekly: { before: "R$ 29,99", after: "R$ 26,99" },
            monthly: { before: "R$ 59,90", after: "R$ 53,91" },
        },
        benefits: [
            { pt: "Kit Apoiador", en: "Supporter Kit" },
            { pt: "Kit Bonus", en: "Bonus Kit" },
            B_LARGE_BACKPACK,
            B_AUTO_FARM,
            B_NO_POWER,
            { pt: "Recicladora na sua base!", en: "Recycler at your base!" },
            B_XP_COINS,
            {
                pt: "Acesso ao ZaroAI no discord e outras funcionalidades novas.",
                en: "Access to ZaroAI on Discord and other new features.",
            },
        ],
        kitSections: [
            {
                title: null,
                items: [
                    {
                        pt: "1x Rifle de Assalto (carregador estendido, mira holográfica e mira laser)",
                        en: "1x Assault Rifle (Extended Magazine, Holosight and Weapon Lasersight)",
                    },
                    {
                        pt: "1x Rifle L96 (mira 8x e mira laser)",
                        en: "1x L96 Rifle (8x Zoom Scope and Weapon Lasersight)",
                    },
                    { pt: "100x Bandagens", en: "100x Bandages" },
                    { pt: "100x Seringas Médicas", en: "100x Medical Syringes" },
                    { pt: "3x Kits Médicos Grandes", en: "3x Large Medkits" },
                    { pt: "100x Carne de Felino Cozinhada", en: "100x Cooked Big Cat Meat" },
                    { pt: "200x Munições de Rifle 5.56", en: "200x 5.56 Rifle Ammo" },
                    { pt: "100x Munição 5.56 Incendiária de Rifle", en: "100x Incendiary 5.56 Rifle Ammo" },
                    { pt: "1000x Munição Antiaérea", en: "1000x SAM Ammo" },
                    { pt: "2x Sistemas Antiaéreos (SAM)", en: "2x SAM Sites" },
                    { pt: "3x Sentinelas Automáticas", en: "3x Auto Turrets" },
                    { pt: "6x Fornalhas Elétricas", en: "6x Electric Furnaces" },
                    { pt: "2x Fornalhas Grandes", en: "2x Large Furnaces" },
                    { pt: "1x Gerador Pequeno", en: "1x Small Generator" },
                    { pt: "100x Ramificadores Elétricos", en: "100x Electrical Branches" },
                    { pt: "1x Ferramenta de Fio", en: "1x Wire Tool" },
                    { pt: "1000x Metal de Alta Qualidade", en: "1000x High Quality Metal" },
                    { pt: "3000x Pólvora", en: "3000x Gun Powder" },
                    { pt: "2x Caixões", en: "2x Coffins" },
                    { pt: "1x Sinalizador de Suprimentos", en: "1x Supply Signal" },
                ],
            },
            {
                title: KIT_EQUIP_TITLE,
                items: [
                    { pt: "1x Colete de Metal", en: "1x Metal Chest Plate" },
                    { pt: "1x Botas", en: "1x Boots" },
                    { pt: "1x Calça", en: "1x Pants" },
                    { pt: "1x Blusão", en: "1x Hoodie" },
                    { pt: "1x Luvas Táticas", en: "1x Tactical Gloves" },
                    { pt: "1x Máscara de Metal", en: "1x Metal Facemask" },
                    { pt: "1x Saia de Placas de Trânsito", en: "1x Road Sign Kilt" },
                    ITEM_LARGE_BACKPACK,
                ],
            },
        ],
        accent: "#FFD45A",
        highlight: { pt: "O mais valioso e comprado", en: "Most valuable & best-selling" },
    },
    {
        id: "personalizado",
        name: { pt: "VIP Personalizado", en: "Custom VIP" },
        line: {
            pt: "Monta o teu kit, benefícios e budget — validado com a equipa antes de activar.",
            en: "Build your own kit, perks and budget — reviewed by the team before activation.",
        },
        customSimulator: true,
        benefits: [
            { pt: "Simulador de inventário estilo Rust", en: "Rust-style inventory simulator" },
            {
                pt: "Escolhe benefícios e budget estimado (desbloqueios)",
                en: "Pick perks and an estimated budget (unlocks)",
            },
            {
                pt: "Preço e regras finais negociados com os admins",
                en: "Final price and terms negotiated with the admins",
            },
        ],
        kitSections: [],
        accent: "#E8A656",
        highlight: { pt: "Sob medida", en: "Tailor-made" },
    },
];

/** Posição na grelha 6 colunas (lg+): 3 cards em cima, 2 centrados em baixo (mesma largura 2/6 cada). */
const VIP_CARD_GRID_CLASS = [
    "lg:col-span-2 lg:col-start-1 lg:row-start-1",
    "lg:col-span-2 lg:col-start-3 lg:row-start-1",
    "lg:col-span-2 lg:col-start-5 lg:row-start-1",
    "lg:col-span-2 lg:col-start-2 lg:row-start-2",
    "lg:col-span-2 lg:col-start-4 lg:row-start-2",
];

/** Altura mínima partilhada — em lg a grelha estica com items-stretch. */
const VIP_CARD_MIN_H = "min-h-[30rem] sm:min-h-[32rem] lg:min-h-0";

/** Hover nos cards (sem flip). */
const VIP_FACE_HOVER =
    "transition-[border-color,box-shadow] duration-300 hover:border-gold/45 hover:shadow-[0_0_0_1px_rgba(255,184,0,0.12),0_20px_48px_-28px_rgba(255,184,0,0.2)]";

function getVipPriceDisplay(tier, monthly) {
    if (tier.promo) {
        const slot = monthly ? tier.promo.monthly : tier.promo.weekly;
        return {
            current: slot.after,
            before: slot.before,
            promoLabel: tier.promo.label,
        };
    }
    return {
        current: monthly ? tier.monthly : tier.weekly,
        before: null,
        promoLabel: null,
    };
}

/** `isolation` cria um stacking context: a camada de arte (z-index -1) fica por cima do gradiente e por baixo do conteúdo. */
const cardSurface = (highlight) => ({
    isolation: "isolate",
    background: highlight
        ? "linear-gradient(180deg, rgba(42,24,16,0.97), rgba(10,7,6,0.94))"
        : "linear-gradient(180deg, rgba(26,15,10,0.94), rgba(10,7,6,0.9))",
    boxShadow: highlight
        ? "0 0 0 1px rgba(255,184,0,0.15), 0 30px 60px -30px rgba(255,184,0,0.25)"
        : "none",
});

function VipCard({ t, index, onOpenKit }) {
    const { t: tx, l } = useI18n();
    const [monthly, setMonthly] = useState(true);
    const tierName = l(t.name);
    const { current: price, before: priceBefore, promoLabel } = getVipPriceDisplay(t, monthly);
    const isGold =
        t.id === "ouro" || t.id === "apoiador" || t.id === "personalizado";

    const borderCls = t.highlight ? "border-gold/40" : "border-white/10";

    if (t.customSimulator) {
        return (
            <ScrollReveal
                delay={index * 100}
                className="reveal-vip flex h-full min-h-0 min-w-0 flex-col"
            >
                <div className="relative flex h-full min-w-0 flex-1 flex-col pt-9 sm:pt-10">
                    {t.highlight && (
                        <div className="pointer-events-none absolute left-1/2 top-3 z-30 -translate-x-1/2 -translate-y-1/2">
                            <div
                                className="max-w-[min(100vw-2rem,16rem)] px-3 py-1 text-center text-[9px] font-bold uppercase leading-tight tracking-[0.12em] whitespace-normal rounded-full sm:text-[10px] sm:tracking-[0.18em]"
                                style={{
                                    background: "#FFB800",
                                    color: "#050505",
                                    boxShadow:
                                        "0 0 24px rgba(255,184,0,0.45), 0 2px 8px rgba(0,0,0,0.35)",
                                }}
                            >
                                {l(t.highlight)}
                            </div>
                        </div>
                    )}

                    <div
                        className={`flex flex-1 flex-col ${VIP_CARD_MIN_H}`}
                        data-testid={`vip-card-${t.id}`}
                    >
                        <div
                            className={`flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl relative border p-6 pb-5 md:p-7 md:pb-6 ${VIP_FACE_HOVER} ${borderCls}`}
                            style={cardSurface(!!t.highlight)}
                        >
                            <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 bg-cover bg-center"
                                style={{
                                    backgroundImage: `url(${VIP_ART[t.id]})`,
                                    opacity: 0.2,
                                    zIndex: -1,
                                }}
                            />
                            <div className="mb-5 flex items-start gap-3.5">
                                <div className="min-w-0 flex-1 pt-0.5">
                                    <div
                                        className="mb-3 h-1 w-10 rounded-full"
                                        style={{ background: t.accent }}
                                    />
                                    <h3 className="text-xl font-bold leading-tight text-zrs-offwhite sm:text-2xl">
                                        {tierName}
                                    </h3>
                                    <p className="mt-1 text-[13px] leading-snug text-zrs-metal">
                                        {l(t.line)}
                                    </p>
                                </div>
                            </div>

                            <div className="mb-5 h-[42px] shrink-0" aria-hidden />

                            <div className="mb-5">
                                <div
                                    data-testid={`vip-${t.id}-price`}
                                    className={`text-2xl font-bold leading-tight sm:text-3xl md:text-4xl ${
                                        isGold ? "text-gold" : "text-zrs-offwhite"
                                    }`}
                                >
                                    {tx("vip.custom.price")}
                                </div>
                                <div className="mt-2 text-[11px] uppercase tracking-[0.25em] text-zrs-metal">
                                    {tx("vip.custom.priceNote")}
                                </div>
                            </div>

                            <p className="mb-2 shrink-0 text-[11px] uppercase tracking-[0.2em] text-zrs-metal">
                                {tx("vip.benefits")}
                            </p>
                            <ul className="mb-4 min-h-0 flex-1 space-y-2.5 overflow-y-auto pr-0.5">
                                {t.benefits.map((b, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-2.5 text-sm leading-snug text-zrs-offwhite/85"
                                    >
                                        <Check
                                            size={14}
                                            className="mt-0.5 shrink-0"
                                            style={{ color: t.accent }}
                                        />
                                        <span>{l(b)}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-auto shrink-0 pt-1">
                                <Link
                                    to="/vip/personalizado"
                                    data-testid={`vip-${t.id}-criar-vip`}
                                    className="btn-shimmer flex w-full items-center justify-center rounded-xl py-3 text-[13px] font-bold tracking-wide transition-transform hover:scale-[1.02]"
                                    style={{
                                        background: "#FFB800",
                                        color: "#050505",
                                    }}
                                >
                                    {tx("vip.custom.cta")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollReveal>
        );
    }

    return (
        <ScrollReveal
            delay={index * 100}
            className="reveal-vip flex h-full min-h-0 min-w-0 flex-col"
        >
            <div className="relative flex h-full min-w-0 flex-1 flex-col pt-9 sm:pt-10">
                {t.highlight && (
                    <div className="pointer-events-none absolute left-1/2 top-3 z-30 -translate-x-1/2 -translate-y-1/2">
                        <div
                            className="max-w-[min(100vw-2rem,16rem)] px-3 py-1 text-center text-[9px] font-bold uppercase leading-tight tracking-[0.12em] whitespace-normal rounded-full sm:text-[10px] sm:tracking-[0.18em]"
                            style={{
                                background: "#FFB800",
                                color: "#050505",
                                boxShadow:
                                    "0 0 24px rgba(255,184,0,0.45), 0 2px 8px rgba(0,0,0,0.35)",
                            }}
                        >
                            {l(t.highlight)}
                        </div>
                    </div>
                )}

                <div
                    className={`flex flex-1 flex-col ${VIP_CARD_MIN_H}`}
                    data-testid={`vip-card-${t.id}`}
                >
                    <div
                        className={`flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl relative border p-6 pb-5 md:p-7 md:pb-6 ${VIP_FACE_HOVER} ${borderCls}`}
                        style={cardSurface(!!t.highlight)}
                    >
                        <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0 bg-cover bg-center"
                            style={{
                                backgroundImage: `url(${VIP_ART[t.id]})`,
                                opacity: 0.2,
                                zIndex: -1,
                            }}
                        />
                        <div className="mb-5 flex gap-3.5 items-start">
                            <div className="min-w-0 flex-1 pt-0.5">
                                <div
                                    className="mb-3 h-1 w-10 rounded-full"
                                    style={{ background: t.accent }}
                                />
                                <h3 className="text-xl font-bold leading-tight text-zrs-offwhite sm:text-2xl">
                                    {tierName}
                                </h3>
                                <p className="mt-1 text-[13px] leading-snug text-zrs-metal">
                                    {l(t.line)}
                                </p>
                            </div>
                        </div>

                        <div
                            className="relative mb-5 flex rounded-full border border-white/10 p-1"
                            style={{ background: "rgba(0,0,0,0.3)" }}
                        >
                            <button
                                type="button"
                                onClick={() => setMonthly(false)}
                                data-testid={`vip-${t.id}-weekly-toggle`}
                                className={`flex-1 rounded-full py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                                    !monthly
                                        ? "text-zrs-black"
                                        : "text-zrs-offwhite/60"
                                }`}
                                style={{
                                    background: !monthly
                                        ? "#FFB800"
                                        : "transparent",
                                }}
                            >
                                {tx("vip.weekly")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setMonthly(true)}
                                data-testid={`vip-${t.id}-monthly-toggle`}
                                className={`flex-1 rounded-full py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                                    monthly
                                        ? "text-zrs-black"
                                        : "text-zrs-offwhite/60"
                                }`}
                                style={{
                                    background: monthly
                                        ? "#FFB800"
                                        : "transparent",
                                }}
                            >
                                {tx("vip.monthly")}
                            </button>
                        </div>

                        <div className="mb-5">
                            {promoLabel ? (
                                <span className="mb-2 inline-block rounded-md border border-gold/35 bg-gold/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gold">
                                    {tx("vip.promo", { label: l(promoLabel) })}
                                </span>
                            ) : null}
                            {priceBefore ? (
                                <div
                                    className="text-base font-semibold text-zrs-metal line-through md:text-lg"
                                    aria-hidden
                                >
                                    {priceBefore}
                                </div>
                            ) : null}
                            <div
                                data-testid={`vip-${t.id}-price`}
                                className={`text-3xl font-bold leading-none md:text-4xl ${
                                    isGold || promoLabel ? "text-gold" : "text-zrs-offwhite"
                                }`}
                            >
                                {price}
                            </div>
                            <div className="mt-2 text-[11px] uppercase tracking-[0.25em] text-zrs-metal">
                                {monthly ? tx("vip.perMonth") : tx("vip.perWeek")}
                            </div>
                            {monthly && t.id === "ouro" ? (
                                <div className="mt-1 text-[11px] text-gold/80">
                                    {tx("vip.bestValue")}
                                </div>
                            ) : null}
                        </div>

                        <p className="mb-2 shrink-0 text-[11px] uppercase tracking-[0.2em] text-zrs-metal">
                            {tx("vip.benefits")}
                        </p>
                        <ul className="mb-4 min-h-0 flex-1 space-y-2.5 overflow-y-auto pr-0.5">
                            {t.benefits.map((b, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-2.5 text-sm leading-snug text-zrs-offwhite/85"
                                >
                                    <Check
                                        size={14}
                                        className="mt-0.5 shrink-0"
                                        style={{ color: t.accent }}
                                    />
                                    <span>{l(b)}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-auto flex shrink-0 flex-col gap-3 pt-1">
                            {getVipKitPlan(t.id) ? (
                                <button
                                    type="button"
                                    onClick={() => onOpenKit(t)}
                                    data-testid={`vip-${t.id}-open-kit`}
                                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 py-2.5 text-[12px] font-semibold tracking-wide text-zrs-offwhite/90 transition-colors hover:border-gold/35 hover:bg-white/[0.04]"
                                >
                                    <Package
                                        size={15}
                                        className="text-gold/90"
                                    />
                                    {tx("vip.viewKit")}
                                </button>
                            ) : null}

                            <a
                                href={VIP_DISCORD_TICKET_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`vip-${t.id}-buy-btn`}
                                className="btn-shimmer inline-flex w-full items-center justify-center rounded-xl py-3 text-[13px] font-bold tracking-wide no-underline transition-transform hover:scale-[1.02]"
                                style={
                                    t.highlight
                                        ? {
                                              background: "#FFB800",
                                              color: "#050505",
                                          }
                                        : {
                                              background: "transparent",
                                              color: "#FFB800",
                                              border: "1px solid rgba(255,184,0,0.35)",
                                          }
                                }
                            >
                                {tx("vip.subscribe", { name: tierName })}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </ScrollReveal>
    );
}

export default function VipSection() {
    const { t } = useI18n();
    const [kitModalTier, setKitModalTier] = useState(null);
    /** Os planos VIP existem só para o servidor 3x Moded. */
    const SERVER_LABEL = "3x Moded";

    return (
        <section
            id="vips"
            data-testid="vip-section"
            className="relative px-6 py-24 md:px-8 md:py-32"
            style={{
                background:
                    "radial-gradient(ellipse at top, rgba(107,51,36,0.12) 0%, transparent 55%)",
            }}
        >
            <div className="mx-auto max-w-[min(100%,88rem)] px-0 sm:px-1">
                <ScrollReveal>
                    <div className="mb-14 text-center md:mb-16">
                        <div className="mb-3 text-[11px] uppercase tracking-[0.4em] text-gold/80">
                            {t("vip.eyebrow")}
                        </div>
                        <h2 className="text-3xl font-bold text-zrs-offwhite md:text-5xl">
                            {t("vip.title")}
                        </h2>
                        <p className="mx-auto mt-4 max-w-xl text-[14px] text-zrs-offwhite/60">
                            {t("vip.introBefore")}{" "}
                            <span className="text-zrs-offwhite/80">
                                {t("vip.viewKit")}
                            </span>{" "}
                            {t("vip.introAfter")}
                        </p>

                        <div className="mx-auto mt-8 flex w-fit items-center gap-2 rounded-full border border-white/10 bg-black/25 p-1">
                            <span
                                data-testid="vip-server-3x"
                                className="rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-zrs-black"
                                style={{ background: "#FFB800" }}
                            >
                                {SERVER_LABEL}
                            </span>
                        </div>
                    </div>
                </ScrollReveal>

                <div
                    className="grid grid-cols-1 items-stretch gap-8 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-10 lg:grid-cols-6 lg:gap-x-8 lg:gap-y-10"
                    data-testid="vip-plans-grid"
                >
                    {TIERS.map((t, i) => (
                        <div
                            key={t.id}
                            className={`flex min-h-0 min-w-0 flex-col ${VIP_CARD_GRID_CLASS[i] ?? ""}`}
                        >
                            <VipCard
                                t={t}
                                index={i}
                                onOpenKit={setKitModalTier}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <VipKitModal
                open={Boolean(kitModalTier)}
                tier={kitModalTier}
                onClose={() => setKitModalTier(null)}
            />
        </section>
    );
}
